<div class="page">

  <div class="page-header">
    <div>
      <h1>Settings</h1>
      <p>Manage app versions, store links, and static pages</p>
    </div>
    <button class="btn-primary" (click)="save()" [disabled]="isSubmitting || isLoading">
      <span *ngIf="!isSubmitting">Save Changes</span>
      <span *ngIf="isSubmitting" class="saving-row">
        <span class="spinner-sm"></span> Saving...
      </span>
    </button>
  </div>

  <div class="toast-success" *ngIf="successMessage">{{ successMessage }}</div>
  <div class="error-msg"     *ngIf="errorMessage">{{ errorMessage }}</div>

  <div class="loading-state" *ngIf="isLoading">
    <div class="spinner-lg"></div>
    <p>Loading settings...</p>
  </div>

  <ng-container *ngIf="!isLoading">

    <!-- Tabs -->
    <div class="tabs-bar">
      <button class="tab-btn" [class.active]="activeTab === 'app'"     (click)="setTab('app')">App &amp; Versions</button>
      <button class="tab-btn" [class.active]="activeTab === 'terms'"   (click)="setTab('terms')">Terms &amp; Conditions</button>
      <button class="tab-btn" [class.active]="activeTab === 'about'"   (click)="setTab('about')">About Us</button>
      <button class="tab-btn" [class.active]="activeTab === 'privacy'" (click)="setTab('privacy')">Privacy Policy</button>
      <button class="tab-btn" [class.active]="activeTab === 'ai-ads'"  (click)="setTab('ai-ads')">AI Ads</button>
      <button class="tab-btn" [class.active]="activeTab === 'banners'" (click)="setTab('banners')">Promo Banners</button>
      <button class="tab-btn" [class.active]="activeTab === 'guide-video'" (click)="setTab('guide-video')">AI Guide Video</button>
    </div>

    <!-- App & Versions tab -->
    <div class="settings-card" *ngIf="activeTab === 'app'">

      <div class="settings-section">
        <p class="section-title">App Review Mode</p>
        <p class="section-desc">Enable this while the app is under App Store / Google Play review. It hides AI Ads, the AI Chatbot, and Packages &amp; Subscriptions from the mobile app so reviewers don't encounter payment or AI features.</p>
        <label class="toggle-row">
          <input type="checkbox" [(ngModel)]="form.in_review" />
          <span class="toggle-label">App is currently in review</span>
        </label>
      </div>

      <hr class="section-divider" />

      <div class="settings-section">
        <p class="section-title">Network Inspector</p>
        <p class="section-desc">Turns on the app's in-app HTTP inspector (floating bug button + call log) remotely, on every environment — useful to debug a live issue without a rebuild. Leave off otherwise: it shows raw request/response bodies on the device.</p>
        <label class="toggle-row">
          <input type="checkbox" [(ngModel)]="form.network_inspector_enabled" />
          <span class="toggle-label">Network inspector enabled</span>
        </label>
      </div>

      <hr class="section-divider" />

      <div class="settings-section">
        <p class="section-title">Contact phone</p>
        <p class="section-desc">Every “Call us” and “Call to book a viewing” button in the app dials this number. Leave it empty to hide those buttons.</p>
        <div class="form-row">
          <div class="form-group">
            <label>Company phone number</label>
            <input type="tel" [(ngModel)]="form.contact_phone" placeholder="+20 100 000 0000" />
          </div>
        </div>
      </div>

      <hr class="section-divider" />

      <div class="settings-section">
        <p class="section-title">Jeeran AI limit</p>
        <p class="section-desc">How many questions one user can ask the AI per hour, across all their chats. Admins are not limited.</p>
        <div class="form-row">
          <div class="form-group">
            <label>Questions per hour</label>
            <input type="number" min="1" max="1000" step="1" [(ngModel)]="form.ai_messages_per_hour" />
          </div>
        </div>
      </div>

      <hr class="section-divider" />

      <div class="settings-section">
        <p class="section-title">Minimum App Versions</p>
        <p class="section-desc">Users on lower versions will be prompted to update.</p>
        <div class="form-row">
          <div class="form-group">
            <label>iOS Min Version</label>
            <input type="text" [(ngModel)]="form.min_version_ios" placeholder="e.g. 1.0.0" />
          </div>
          <div class="form-group">
            <label>Android Min Version</label>
            <input type="text" [(ngModel)]="form.min_version_android" placeholder="e.g. 1.0.0" />
          </div>
        </div>
      </div>

      <hr class="section-divider" />

      <div class="settings-section">
        <p class="section-title">Store Links</p>
        <p class="section-desc">Deep links shown in update prompts and app onboarding.</p>
        <div class="form-row">
          <div class="form-group full">
            <label>App Store URL (iOS)</label>
            <input type="url" [(ngModel)]="form.app_store_url" placeholder="https://apps.apple.com/..." />
          </div>
        </div>
        <div class="form-row">
          <div class="form-group full">
            <label>Google Play URL (Android)</label>
            <input type="url" [(ngModel)]="form.google_play_url" placeholder="https://play.google.com/..." />
          </div>
        </div>
      </div>

    </div>

    <!-- Terms tab -->
    <div class="settings-card" *ngIf="activeTab === 'terms'">
      <div class="settings-section">
        <p class="section-title">Terms &amp; Conditions</p>
        <p class="section-desc">Accepts full HTML. Use heading and paragraph tags for formatting.</p>
        <div class="html-editors">
          <div class="form-group">
            <label>English</label>
            <textarea [(ngModel)]="form.terms_en" rows="20" placeholder="<h1>Terms</h1><p>...</p>" dir="ltr"></textarea>
          </div>
          <div class="form-group">
            <label>Arabic</label>
            <textarea [(ngModel)]="form.terms_ar"  rows="20" placeholder="<h1>الشروط</h1><p>...</p>" dir="rtl"></textarea>
          </div>
        </div>
      </div>
    </div>

    <!-- About Us tab -->
    <div class="settings-card" *ngIf="activeTab === 'about'">
      <div class="settings-section">
        <p class="section-title">About Us</p>
        <p class="section-desc">Accepts full HTML. Use heading and paragraph tags for formatting.</p>
        <div class="html-editors">
          <div class="form-group">
            <label>English</label>
            <textarea [(ngModel)]="form.about_us_en" rows="20" placeholder="<h1>About Us</h1><p>...</p>" dir="ltr"></textarea>
          </div>
          <div class="form-group">
            <label>Arabic</label>
            <textarea [(ngModel)]="form.about_us_ar" rows="20" placeholder="<h1>من نحن</h1><p>...</p>" dir="rtl"></textarea>
          </div>
        </div>
      </div>
    </div>

    <!-- Privacy Policy tab -->
    <div class="settings-card" *ngIf="activeTab === 'privacy'">
      <div class="settings-section">
        <p class="section-title">Privacy Policy</p>
        <p class="section-desc">Accepts full HTML. Use heading and paragraph tags for formatting.</p>
        <div class="html-editors">
          <div class="form-group">
            <label>English</label>
            <textarea [(ngModel)]="form.privacy_policy_en" rows="20" placeholder="<h1>Privacy Policy</h1><p>...</p>" dir="ltr"></textarea>
          </div>
          <div class="form-group">
            <label>Arabic</label>
            <textarea [(ngModel)]="form.privacy_policy_ar" rows="20" placeholder="<h1>سياسة الخصوصية</h1><p>...</p>" dir="rtl"></textarea>
          </div>
        </div>
      </div>
    </div>

    <!-- AI Ads tab -->
    <div class="settings-card" *ngIf="activeTab === 'ai-ads'">
      <div class="settings-section">
        <p class="section-title">AI Ad Generation</p>
        <p class="section-desc">Control the price per generation and how many free re-generation trials each user gets per ad.</p>
        <div class="form-row">
          <div class="form-group">
            <label>Price per Generation (EGP)</label>
            <input type="number" [(ngModel)]="form.ad_generation_price" min="0" step="0.01" placeholder="50.00" />
          </div>
          <div class="form-group">
            <label>Max Trials per Ad</label>
            <input type="number" [(ngModel)]="form.ad_generation_trials" min="0" step="1" placeholder="5" />
          </div>
        </div>
        <p class="section-desc" style="margin-top: 8px;">
          Trials are free re-generations using the same images but a new caption. Users can request up to the limit above per original paid ad.
        </p>
      </div>
    </div>

    <!-- Promo Banners tab -->
    <div class="settings-card" *ngIf="activeTab === 'banners'">

      <div class="settings-section">
        <p class="section-title">Promotional Banners</p>
        <p class="section-desc">
          Two fixed banners shown on the home screen. Control which ones are visible and which appears first.
          Changes take effect immediately after saving.
        </p>

        <!-- AI Ads Banner -->
        <div class="promo-banner-row">
          <div class="promo-banner-info">
            <span class="promo-banner-label">AI Ads Banner</span>
            <span class="promo-banner-sublabel">Tapping opens the AI Ad Generation screen</span>
          </div>
          <label class="toggle-row">
            <input type="checkbox" [(ngModel)]="form.promo_ai_ads_visible" />
            <span class="toggle-label">Visible</span>
          </label>
        </div>

        <hr class="section-divider" style="margin: 1.25rem 0;" />

        <!-- Seller Banner -->
        <div class="promo-banner-row">
          <div class="promo-banner-info">
            <span class="promo-banner-label">Add a Property Banner</span>
            <span class="promo-banner-sublabel">Tapping opens the add-a-listing flow</span>
          </div>
          <label class="toggle-row">
            <input type="checkbox" [(ngModel)]="form.promo_seller_visible" />
            <span class="toggle-label">Visible</span>
          </label>
        </div>

        <hr class="section-divider" style="margin: 1.25rem 0;" />

        <!-- Display Order -->
        <div class="settings-section" style="padding: 0;">
          <p class="section-title">Display Order</p>
          <p class="section-desc">Which banner appears first on the home screen.</p>
          <div class="order-radios">
            <label class="radio-row">
              <input type="radio" name="promo_order_group" [value]="true" [(ngModel)]="aiAdsFirst" />
              <span>AI Ads banner first</span>
            </label>
            <label class="radio-row">
              <input type="radio" name="promo_order_group" [value]="false" [(ngModel)]="aiAdsFirst" />
              <span>Add a Property banner first</span>
            </label>
          </div>
        </div>

      </div>
    </div>

    <!-- AI Guide Video tab -->
    <div class="settings-card" *ngIf="activeTab === 'guide-video'">

      <div class="settings-section">
        <p class="section-title">AI Ad Guide Video</p>
        <p class="section-desc">
          A one-time intro video shown to users the first time they tap "Generate Ad".
          Upload a video or paste a direct URL. Toggle visibility to enable or disable it.
        </p>

        <!-- Visibility toggle -->
        <label class="toggle-row" style="margin-bottom: 1.25rem;">
          <input type="checkbox" [(ngModel)]="form.ai_guide_video_visible" />
          <span class="toggle-label">Show guide video to users</span>
        </label>

        <hr class="section-divider" />

        <!-- Current URL (editable) -->
        <div class="settings-field" style="margin-top: 1.25rem;">
          <label class="field-label">Video URL</label>
          <input class="field-input" type="url" [(ngModel)]="form.ai_guide_video_url"
                 placeholder="https://..." />
        </div>

        <!-- Upload -->
        <div style="margin-top: 1rem;">
          <p class="section-desc" style="margin-bottom: .5rem;">Or upload a new video (MP4, max 50 MB):</p>
          <app-media-uploader
            folder="uploads"
            accept="video/mp4,video/*"
            (uploaded)="onGuideVideoUploaded($event)">
          </app-media-uploader>
        </div>

        <!-- Preview -->
        <div *ngIf="form.ai_guide_video_url" style="margin-top: 1.25rem;">
          <p class="section-desc" style="margin-bottom: .5rem;">Preview:</p>
          <video [src]="form.ai_guide_video_url" controls style="width:100%; max-height:320px; border-radius:8px;"></video>
        </div>
      </div>
    </div>

  </ng-container>

</div>
